/**
 * Phase A: Display-only Auto-Match zwischen Rechnung.objekt_adresse (Freitext)
 * und der Objekte-Tabelle, ohne die DB zu mutieren.
 *
 * Phase B (geplant): Backfill in DB schreiben + n8n direkt verlinken lassen.
 */

export interface ObjektForMatch {
  id: string;
  objektnummer?: string | null;
  bezeichnung?: string | null;
  strasse?: string | null;
  hausnummer?: string | null;
  plz?: string | null;
  stadt?: string | null;
}

/** Normalisiert Adress-Strings für Vergleiche: lowercase, Umlaute, str.→strasse */
function normalize(s: string | null | undefined): string {
  if (!s) return '';
  return s
    .toLowerCase()
    .replace(/ß/g, 'ss')
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    // "str." vor Punctuation-Cleanup ersetzen
    .replace(/str\./g, 'strasse')
    .replace(/strasse/g, 'strasse') // normalize variations
    .replace(/[.,;:]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Findet das Objekt das am besten zur freien Adresse passt.
 * Match-Strategie:
 *  1. Strasse muss als Substring drin sein (normalisiert)
 *  2. Hausnummer muss als Wort (\b) drin sein
 *  3. Wenn PLZ im Objekt vorhanden, Boost wenn auch in Adresse
 */
export function matchObjektByAdresse(
  adresse: string | null | undefined,
  objekte: ObjektForMatch[]
): ObjektForMatch | null {
  if (!adresse) return null;
  const norm = normalize(adresse);
  if (!norm) return null;

  let bestMatch: ObjektForMatch | null = null;
  let bestScore = 0;

  for (const o of objekte) {
    if (!o.strasse || !o.hausnummer) continue;
    const strasseNorm = normalize(o.strasse);
    const hausnummer = String(o.hausnummer).trim().toLowerCase();
    if (!strasseNorm || !hausnummer) continue;

    // Strasse muss enthalten sein
    if (!norm.includes(strasseNorm)) continue;

    // Hausnummer als eigenes Wort/Token
    const hnPattern = new RegExp(`(?:^|[^0-9a-z])${hausnummer.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?:[^0-9a-z]|$)`);
    if (!hnPattern.test(norm)) continue;

    // Score: Basis 1 für Strasse+Hausnr-Match
    let score = 1;

    // PLZ-Bonus für höhere Sicherheit
    if (o.plz && norm.includes(String(o.plz))) score += 2;
    // Stadt-Bonus
    if (o.stadt && norm.includes(normalize(o.stadt))) score += 1;

    if (score > bestScore) {
      bestScore = score;
      bestMatch = o;
    }
  }

  return bestMatch;
}
